import { getFirestore, doc, updateDoc, addDoc, collection, serverTimestamp, getDoc, getDocs, query, orderBy } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { CSVUtils } from "../../utils/csv-utils.js";
import { UserService } from "../../services/user.service.js"; // For Notifications logic if needed
import { convertToWebP } from "../../utils/image-processor.js";

const db = getFirestore(app);

export class SeriesEditorModal {
    // ... existing constructor ...

    // ... existing open/close/render ...

    async loadEpisodesList() {
        const list = this.container.querySelector('#episodes-list');
        let episodes = this.data.episodes || [];

        // [FIX] Feature Parity: Fetch Subcollection if missing
        // AdminContentService.getAllSeries() does NOT fetch subcollections.
        // So we must fetch them here for the list to be populated.
        if (episodes.length === 0 && this.seriesId) {
            list.innerHTML = `
                    <div class="p-8 text-center text-zinc-500 flex flex-col items-center animate-pulse">
                        <span class="material-symbols-outlined text-3xl mb-2 opacity-50">cloud_sync</span>
                        ${window.locale.t('editor.refreshing_list')}
                    </div>`;

            try {
                const q = query(
                    collection(db, "series", this.seriesId, "episodes")
                    // [FIX] Removed orderBy("order") because Webnovel episodes lack 'order' field.
                    // This prevents them from being filtered out.
                );
                const querySnapshot = await getDocs(q);
                episodes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // [FIX] Client-side Sort (Robust)
                // Handle both 'order' (DaVinci) and 'index' (Webnovel)
                episodes.sort((a, b) => {
                    const orderA = a.order ?? a.index ?? 0;
                    const orderB = b.order ?? b.index ?? 0;
                    return orderA - orderB;
                });

                this.data.episodes = episodes;

            } catch (e) {
                console.error("Failed to load episodes", e);
                list.innerHTML = `<div class="p-8 text-center text-red-500">${window.locale.t('editor.load_fail')}${e.message}</div>`;
                return;
            }
        }

        if (episodes.length === 0) {
            list.innerHTML = `<div class="p-8 text-center text-zinc-500 flex flex-col items-center"><span class="material-symbols-outlined text-3xl mb-2 opacity-50">layers_clear</span>${window.locale.t('editor.no_episodes')}</div>`;
            return;
        }

        list.innerHTML = episodes.map((ep, i) => `
            <div class="p-4 flex items-center justify-between hover:bg-zinc-900 group">
                <div class="flex items-center gap-4">
                    <span class="text-zinc-500 font-mono text-xs w-6 text-center">${i + 1}</span>
                    <div class="w-10 h-10 bg-black rounded overflow-hidden border border-zinc-700">
                        ${(() => {
                const img = ep.thumb || ep.thumbnail || ep.image || ep.displayImage || ep.coverImage || ep.imageURL || this.data.coverImage;
                return (typeof img === 'string' && img) ? `<img src="${img}" class="w-full h-full object-cover">` : '';
            })()}
                    </div>
                    <div>
                        <h4 class="text-white font-bold text-sm">${ep.title}</h4>
                        <span class="text-xs text-zinc-500">
                            ${ep.status === 'published'
                ? `<span class="text-green-500">${window.locale.t('editor.status_published')}</span>`
                : `<span class="text-amber-500">${window.locale.t('editor.status_draft')}</span>`}
                            • ${ep.updatedAt?.toDate ? ep.updatedAt.toDate().toLocaleDateString(window.locale.current, { year: 'numeric', month: 'numeric', day: 'numeric' }) : 'Just now'}
                        </span>
                    </div>
                </div>
                <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <!-- Actions visible only in full editor mostly -->
                     <span class="text-xs text-zinc-600">${window.locale.t('editor.edit_detail_guide')}</span>
                </div>
            </div>
        `).join('');
    }
    constructor() {
        this.container = null;
        this.mode = 'create'; // create | edit
        this.seriesId = null;
        this.data = {};
        this.onCloseCallback = null;
    }

    /**
     * Open the modal
     * @param {string} mode - 'create' or 'edit'
     * @param {object|null} seriesData - Existing data for edit mode
     * @param {string|null} seriesId - ID for edit mode
     * @param {function} onClose - Callback after save/close
     */
    async open(mode, seriesData = null, seriesId = null, onClose = null) {
        this.mode = mode;
        this.data = seriesData || { title: '', type: 'webtoon', tags: [], isAdult: false, isAI: false };
        this.seriesId = seriesId;
        this.onCloseCallback = onClose;

        // If edit mode and no internalId, generate one for display (not save yet)
        if (this.mode === 'edit' && !this.data.internalId) {
            // We don't save it here, just prepared. 
            // Actually Admin might want to assign it.
        }

        this.render();
        document.body.appendChild(this.container);

        // Animate In
        requestAnimationFrame(() => {
            this.container.querySelector('.backdrop').classList.remove('opacity-0');
            this.container.querySelector('.modal-box').classList.remove('opacity-0', 'scale-95');
        });
    }

    close() {
        if (!this.container) return;
        const backdrop = this.container.querySelector('.backdrop');
        const box = this.container.querySelector('.modal-box');

        backdrop.classList.add('opacity-0');
        box.classList.add('opacity-0', 'scale-95');

        setTimeout(() => {
            if (this.container && this.container.parentNode) {
                this.container.parentNode.removeChild(this.container);
            }
            this.container = null;
            if (this.onCloseCallback) this.onCloseCallback();
        }, 300);
    }

    render() {
        const isAdmin = window.user && (window.user.isSuperAdmin || ['admin', 'super_admin'].includes(window.user.role));

        this.container = document.createElement('div');
        this.container.className = 'fixed inset-0 z-[9999] flex items-center justify-center isolate';
        this.container.innerHTML = `
            <div class="backdrop absolute inset-0 bg-black/80 backdrop-blur-sm opacity-0 transition-opacity duration-300"></div>
            <div class="modal-box relative bg-zinc-900 border border-zinc-700 w-full max-w-4xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden opacity-0 scale-95 transition-all duration-300 transform">
                
                <!-- Header -->
                <div class="h-16 flex items-center justify-between px-6 border-b border-zinc-800 bg-zinc-950">
                    <h2 class="text-xl font-bold text-white flex items-center gap-2">
                        <span class="material-symbols-outlined text-purple-500">${this.mode === 'create' ? 'add_circle' : 'edit_document'}</span>
                        ${this.mode === 'create' ? window.locale.t('editor.create_series') : window.locale.t('editor.edit_series')}
                    </h2>
                    <button class="btn-close text-zinc-500 hover:text-white transition-colors">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>

                <!-- Tabs (Edit Mode Only) -->
                ${this.mode === 'edit' ? `
                <div class="flex items-center gap-1 px-6 pt-4 border-b border-zinc-800 bg-zinc-900/50">
                    <button class="tab-btn active px-4 py-2 text-sm font-bold border-b-2 border-purple-500 text-purple-400" data-tab="info">${window.locale.t('editor.basic_info')}</button>
                    <button class="tab-btn px-4 py-2 text-sm font-bold border-b-2 border-transparent text-zinc-500 hover:text-zinc-300" data-tab="episodes">${window.locale.t('editor.episode_mgmt')}</button>
                    ${isAdmin ? `<button class="tab-btn px-4 py-2 text-sm font-bold border-b-2 border-transparent text-zinc-500 hover:text-zinc-300" data-tab="admin">${window.locale.t('editor.admin_id')}</button>` : ''}
                </div>
                ` : ''}

                <!-- Content Area -->
                <div class="flex-grow overflow-y-auto p-0 bg-zinc-900 relative">
                    
                    <!-- TAB: INFO (Always Visible in Create Mode) -->
                    <div id="tab-info" class="tab-content p-8 space-y-8 animate-fade-in block">
                        <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
                            <!-- Left: Cover Preview -->
                            <div class="md:col-span-4 space-y-4">
                                <label class="block text-xs font-bold text-zinc-500 uppercase">${window.locale.t('editor.cover_image')}</label>
                                <div class="aspect-[2/3] w-full bg-black rounded-xl border border-zinc-700 overflow-hidden relative group">
                                    <img id="preview-cover" src="${this.data.coverImage || ''}" class="w-full h-full object-cover ${this.data.coverImage ? '' : 'hidden'}">
                                    <div class="${this.data.coverImage ? 'hidden' : ''} w-full h-full flex flex-col items-center justify-center text-zinc-600">
                                        <span class="material-symbols-outlined text-4xl mb-2">image</span>
                                        <span class="text-xs">${window.locale.t('editor.no_cover')}</span>
                                    </div>
                                    
                                    <!-- Badges Overlay -->
                                    <div id="badge-19" class="absolute top-2 right-2 bg-red-600 text-white text-xs font-black px-2 py-0.5 rounded ${this.data.isAdult ? '' : 'hidden'}">19</div>
                                    <div id="badge-ai" class="absolute bottom-2 left-2 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded border border-white/20 ${this.data.isAI ? '' : 'hidden'}">${window.locale.t('editor.badge_ai')}</div>

                                    <!-- Upload Overlay -->
                                    <label class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                                        <span class="material-symbols-outlined text-white text-3xl">upload</span>
                                        <input type="file" id="input-cover-file" class="hidden" accept="image/*">
                                    </label>
                                </div>
                                <input type="text" id="input-cover-url" class="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-xs text-zinc-400 font-mono" placeholder="${window.locale.t('editor.cover_url') || 'Image URL'}" value="${this.data.coverImage || ''}">
                            </div>

                            <!-- Right: Fields -->
                            <div class="md:col-span-8 space-y-5">

                                <div class="space-y-2">
                                    <label class="block text-xs font-bold text-zinc-400 uppercase">${window.locale.t('editor.title')} <span class="text-red-500">*</span></label>
                                    <input type="text" id="input-title" class="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white focus:border-purple-500 outline-none font-bold text-lg placeholder-zinc-600" value="${this.data.title || ''}" placeholder="${window.locale.t('editor.title_placeholder') || 'Enter Series Title'}">
                                </div>

                                <!-- (Display Mode Removed) -->

                                    <div class="col-span-2 space-y-2">
                                        <label class="block text-xs font-bold text-zinc-400 uppercase mb-1">
                                            ${window.locale.t('editor.format')} <span class="text-red-500">*</span>
                                            ${this.mode === 'edit' ? `<span class="text-xs text-rose-500 ml-2 font-normal"><span class="material-symbols-outlined text-[12px] align-middle mr-1">lock</span>${window.locale.t('editor.format_locked')}</span>` : ''}
                                        </label>
                                        <div class="grid grid-cols-3 gap-3 ${this.mode === 'edit' ? 'opacity-60 grayscale pointer-events-none' : ''}" id="type-radio-group">
                                            <label class="cursor-pointer group relative">
                                                <input type="radio" name="series-type" value="webtoon" class="peer hidden" ${this.data.type === 'webtoon' ? 'checked' : ''} ${this.mode === 'edit' ? 'disabled' : ''}>
                                                <div class="bg-zinc-800 border-2 border-zinc-700 peer-checked:border-purple-500 peer-checked:bg-purple-500/10 rounded-xl p-4 flex flex-col items-center justify-center transition-all group-hover:border-zinc-600 hover:shadow-lg h-full">
                                                    <span class="material-symbols-outlined text-3xl text-zinc-500 peer-checked:text-purple-400 mb-2 transition-colors">collections_bookmark</span>
                                                    <span class="text-sm font-bold text-zinc-400 peer-checked:text-white transition-colors">${window.locale.t('editor.webtoon')}</span>
                                                </div>
                                                <div class="absolute top-2 right-2 w-4 h-4 rounded-full border border-zinc-600 peer-checked:bg-purple-500 peer-checked:border-purple-500 transition-colors"></div>
                                            </label>
                                            <label class="cursor-pointer group relative">
                                                <input type="radio" name="series-type" value="webnovel" class="peer hidden" ${this.data.type === 'webnovel' ? 'checked' : ''} ${this.mode === 'edit' ? 'disabled' : ''}>
                                                <div class="bg-zinc-800 border-2 border-zinc-700 peer-checked:border-indigo-500 peer-checked:bg-indigo-500/10 rounded-xl p-4 flex flex-col items-center justify-center transition-all group-hover:border-zinc-600 hover:shadow-lg h-full">
                                                    <span class="material-symbols-outlined text-3xl text-zinc-500 peer-checked:text-indigo-400 mb-2 transition-colors">menu_book</span>
                                                    <span class="text-sm font-bold text-zinc-400 peer-checked:text-white transition-colors">${window.locale.t('editor.webnovel')}</span>
                                                </div>
                                                <div class="absolute top-2 right-2 w-4 h-4 rounded-full border border-zinc-600 peer-checked:bg-indigo-500 peer-checked:border-indigo-500 transition-colors"></div>
                                            </label>
                                            <label class="cursor-pointer group relative">
                                                <input type="radio" name="series-type" value="davinci" class="peer hidden" ${this.data.type === 'davinci' ? 'checked' : ''} ${this.mode === 'edit' ? 'disabled' : ''}>
                                                <div class="bg-zinc-800 border-2 border-zinc-700 peer-checked:border-rose-500 peer-checked:bg-rose-500/10 rounded-xl p-4 flex flex-col items-center justify-center transition-all group-hover:border-zinc-600 hover:shadow-lg h-full">
                                                    <span class="material-symbols-outlined text-3xl text-zinc-500 peer-checked:text-rose-400 mb-2 transition-colors">movie</span>
                                                    <span class="text-sm font-bold text-zinc-400 peer-checked:text-white transition-colors">${window.locale.t('editor.cinenovel')}</span>
                                                </div>
                                                <div class="absolute top-2 right-2 w-4 h-4 rounded-full border border-zinc-600 peer-checked:bg-rose-500 peer-checked:border-rose-500 transition-colors"></div>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div class="col-span-2 space-y-2 mt-2">
                                        <label class="block text-xs font-bold text-zinc-400 uppercase">${window.locale.t('editor.tags')}</label>
                                        <input type="text" id="input-tags" class="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white focus:border-purple-500 outline-none text-sm placeholder-zinc-600" value="${(this.data.tags || []).join(', ')}" placeholder="${window.locale.t('editor.tags')}">
                                    </div>

                                <div class="space-y-2">
                                    <label class="block text-xs font-bold text-zinc-400 uppercase">${window.locale.t('studio.lab_desc') || "Description"}</label>
                                    <textarea id="input-desc" rows="4" class="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white focus:border-purple-500 outline-none resize-none leading-relaxed text-sm" placeholder="${window.locale.t('editor.desc_placeholder')}">${this.data.description || ''}</textarea>
                                </div>

                                <!-- Compliance Section -->
                                <div class="bg-zinc-950 border border-zinc-800 rounded-xl p-5 space-y-4">
                                    <h4 class="text-sm font-bold text-zinc-300 flex items-center gap-2">
                                        <span class="material-symbols-outlined text-yellow-500">verified_user</span> ${window.locale.t('editor.compliance')}
                                    </h4>
                                    
                                    <!-- Age Rating Selector -->
                                    <div class="space-y-3">
                                        <div class="flex items-center justify-between">
                                            <h4 class="text-xs font-bold text-zinc-400 uppercase">${window.locale.t('editor.age_rating')} <span class="text-red-500">*</span></h4>
                                            <button id="btn-age-guide" class="text-[10px] font-bold text-purple-400 hover:text-purple-300 flex items-center gap-1">
                                                <span class="material-symbols-outlined text-[12px]">help</span> ${window.locale.t('editor.age_guide')}
                                            </button>
                                        </div>
                                        
                                        <div class="grid grid-cols-4 gap-2" id="age-rating-group">
                                            ${[0, 12, 15, 19].map(limit => {
            // Check logic: existing 'ageLimit' OR fallback 'isAdult' -> 19
            const currentLimit = this.data.ageLimit !== undefined ? this.data.ageLimit : (this.data.isAdult ? 19 : 0);
            const isChecked = currentLimit === limit;

            // [FIX] Lock if already set in Edit Mode
            const isLocked = this.mode === 'edit' && this.data.ageLimit !== undefined && !isAdmin;
            // Admin can bypass lock? User said "Only Admin can change". 
            // Correct. If isAdmin is true, isLocked false.

            let colorClass = "peer-checked:border-green-500 peer-checked:text-green-500";
            let label = window.locale.t('editor.age_all');
            if (limit === 12) { colorClass = "peer-checked:border-blue-500 peer-checked:text-blue-500"; label = window.locale.t('editor.age_12'); }
            if (limit === 15) { colorClass = "peer-checked:border-amber-500 peer-checked:text-amber-500"; label = window.locale.t('editor.age_15'); }
            if (limit === 19) { colorClass = "peer-checked:border-red-500 peer-checked:text-red-500"; label = window.locale.t('editor.age_19'); }

            // Visual Dimming if locked
            const opacityClass = (isLocked && !isChecked) ? 'opacity-30 grayscale cursor-not-allowed' : '';

            return `
                                                <label class="cursor-pointer group relative ${opacityClass}">
                                                    <input type="radio" name="age-limit" value="${limit}" class="peer hidden" ${isChecked ? 'checked' : ''} ${isLocked ? 'disabled' : ''}>
                                                    <div class="bg-zinc-900 border border-zinc-700 rounded-xl p-3 flex flex-col items-center justify-center transition-all group-hover:border-zinc-500 hover:bg-zinc-800 ${colorClass}">
                                                        <span class="text-lg font-black tracking-tight">${label}</span>
                                                        <span class="text-[10px] text-zinc-500 font-medium">${window.locale.t('editor.rating_label')}</span>
                                                    </div>
                                                </label>
                                                `;
        }).join('')}
                                        </div>
                                        <div class="space-y-1">
                                            <p class="text-[11px] text-zinc-500" id="age-desc">
                                                ${window.locale.t('editor.age_19_desc')}
                                            </p>
                                            <p class="text-[11px] text-red-500 font-bold flex items-center gap-1">
                                                <span class="material-symbols-outlined text-[12px]">lock</span>
                                                ${window.locale.t('editor.age_lock_warning')}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="flex items-start gap-3 p-3 rounded-lg hover:bg-zinc-900/50 transition-colors cursor-pointer select-none" id="check-group-ai">
                                        <input type="checkbox" id="input-is-ai" class="mt-1 w-5 h-5 rounded border-zinc-600 text-purple-600 focus:ring-purple-500 bg-zinc-800" ${this.data.isAI ? 'checked' : ''}>
                                        <div>
                                            <label for="input-is-ai" class="font-bold text-sm text-zinc-400 block cursor-pointer">${window.locale.t('editor.is_ai')}</label>
                                            <p class="text-xs text-zinc-500 mt-1">${window.locale.t('editor.is_ai_desc')}</p>
                                        </div>
                                    </div>
                                </div>

                                
                                <!-- ISBN/UCI Section (Admins Only) -->
                                ${isAdmin ? `
                                <div class="bg-zinc-950 border border-yellow-500/20 rounded-xl p-5 space-y-4 relative overflow-hidden">
                                     <div class="absolute top-0 right-0 p-2 opacity-10 pointer-events-none">
                                        <span class="material-symbols-outlined text-6xl text-yellow-500">lock_person</span>
                                    </div>
                                    <h4 class="text-sm font-bold text-yellow-500 flex items-center gap-2">
                                        <span class="material-symbols-outlined">qr_code_scanner</span> ${window.locale.t('editor.admin_id')}
                                    </h4>
                                    <div class="grid grid-cols-2 gap-4 relative z-10">
                                        <div class="space-y-2">
                                            <label class="block text-xs font-bold text-zinc-400 uppercase">ISBN</label>
                                            <input type="text" id="input-isbn" class="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white font-mono placeholder-zinc-700 focus:border-yellow-500 outline-none" 
                                                value="${this.data.isbn || ''}" 
                                                placeholder="978-...">
                                        </div>
                                        <div class="space-y-2">
                                            <label class="block text-xs font-bold text-zinc-400 uppercase">UCI</label>
                                            <input type="text" id="input-uci" class="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white font-mono placeholder-zinc-700 focus:border-yellow-500 outline-none" 
                                                value="${this.data.uci || ''}" 
                                                placeholder="I410-...">
                                        </div>
                                    </div>
                                    <div class="mt-4 pt-4 border-t border-yellow-500/10 space-y-2">
                                        <label class="block text-xs font-bold text-yellow-500 uppercase flex items-center gap-2">
                                            <span class="material-symbols-outlined text-sm">monetization_on</span> ${window.locale.t('editor.free_limit')}
                                        </label>
                                        <div class="flex items-center gap-2">
                                            <input type="number" id="input-free-limit" class="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white font-mono placeholder-zinc-700 focus:border-yellow-500 outline-none" 
                                                value="${this.data.customFreeLimit ?? ''}" 
                                                placeholder="${window.locale.t('editor.free_limit_placeholder')}">
                                        </div>
                                        <p class="text-[11px] text-zinc-500">${window.locale.t('editor.free_limit_desc')}</p>
                                    </div>
                                    <p class="text-[11px] text-zinc-500">${window.locale.t('editor.admin_edit_anytime')}</p>
                                </div>
                                ` : ''}
                            </div>
                        </div>
                    </div>

                    <!-- TAB: EPISODES (Edit Mode Only) -->
                    ${this.mode === 'edit' ? `
                    <div id="tab-episodes" class="tab-content hidden p-6 max-w-4xl mx-auto">
                        <div class="flex justify-between items-center mb-6">
                            <h3 class="text-lg font-bold text-white">${window.locale.t('editor.episode_list')}</h3>
                            <button id="btn-launch-editor" class="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg hover:shadow-indigo-500/20 transition-all">
                                <span class="material-symbols-outlined text-[18px]">open_in_new</span>
                                ${window.locale.t('editor.open_editor_fullscreen')}
                            </button>
                        </div>
                        
                        <div class="bg-zinc-950 rounded-xl border border-zinc-800 overflow-hidden">
                            <div id="episodes-list" class="divide-y divide-zinc-800 max-h-[500px] overflow-y-auto">
                                <!-- Episodes injected via JS -->
                                <div class="p-8 text-center text-zinc-500">
                                    Loading episodes...
                                </div>
                            </div>
                        </div>
                        <p class="text-xs text-zinc-500 mt-4 text-center">
                            ${window.locale.t('editor.editor_guide_text')}
                        </p>
                    </div>
                    ` : ''}

                    <!-- TAB: ADMIN (Edit Mode + Admin Only) -->
                    ${this.mode === 'edit' && isAdmin ? `
                    <div id="tab-admin" class="tab-content hidden p-8 max-w-3xl mx-auto space-y-6">
                         <div class="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex gap-4">
                            <span class="material-symbols-outlined text-yellow-500">lock</span>
                            <div>
                                <h4 class="font-bold text-yellow-500 text-sm">${window.locale.t('editor.admin_zone')}</h4>
                                <p class="text-xs text-yellow-500/80">${window.locale.t('editor.admin_zone_desc')}</p>
                            </div>
                        </div>

                        <div class="space-y-4">
                            <div class="space-y-2">
                                <label class="block text-xs font-bold text-zinc-400 uppercase">Internal ID (System)</label>
                                <input type="text" value="${this.seriesId}" readonly class="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-zinc-500 font-mono cursor-not-allowed select-all">
                            </div>
                            
                            <div class="space-y-2">
                                <label class="block text-xs font-bold text-zinc-400 uppercase">Internal ID (System)</label>
                                <input type="text" value="${this.seriesId}" readonly class="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-zinc-500 font-mono cursor-not-allowed select-all">
                            </div>
                        </div>

                        <div class="pt-6 border-t border-zinc-800">
                            <button id="btn-download-csv" class="w-full py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">
                                <span class="material-symbols-outlined">download</span> ${window.locale.t('editor.download_csv')}
                            </button>
                        </div>
                    </div>
                    ` : ''}

                </div>

                <!-- Footer -->
                <div class="p-4 border-t border-zinc-800 bg-zinc-950 flex justify-end gap-3 z-10">
                    <button class="btn-cancel px-6 py-2.5 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors font-bold text-sm">${window.locale.t('editor.cancel')}</button>
                    <button class="btn-save px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/20 font-bold text-sm transition-all active:scale-[0.98] flex items-center gap-2">
                        <span class="material-symbols-outlined text-[18px]">save</span> 
                        ${this.mode === 'create' ? window.locale.t('editor.create') : window.locale.t('editor.save')}
                    </button>
                </div>
            </div>
        `;

        this.bindEvents(isAdmin);
    }

    bindEvents(isAdmin) {
        // Closes
        this.container.querySelector('.btn-close').onclick = () => this.close();
        this.container.querySelector('.btn-cancel').onclick = () => this.close();
        this.container.querySelector('.backdrop').onclick = () => this.close();

        // Tabs
        const tabs = this.container.querySelectorAll('.tab-btn');
        const contents = this.container.querySelectorAll('.tab-content');
        tabs.forEach(btn => {
            btn.onclick = () => {
                tabs.forEach(t => {
                    t.classList.remove('active', 'border-purple-500', 'text-purple-400');
                    t.classList.add('border-transparent', 'text-zinc-500');
                });
                btn.classList.add('active', 'border-purple-500', 'text-purple-400');
                btn.classList.remove('border-transparent', 'text-zinc-500');

                const targetId = `tab-${btn.dataset.tab}`;
                contents.forEach(c => {
                    if (c.id === targetId) c.classList.remove('hidden');
                    else c.classList.add('hidden');
                });

                // Load episodes if tab is episodes
                if (btn.dataset.tab === 'episodes') {
                    this.loadEpisodesList();
                }
            };
        });

        // Compliance Logic (Visual UI)
        const ageRadios = this.container.querySelectorAll('input[name="age-limit"]');
        const badgeAdult = this.container.querySelector('#badge-19');

        ageRadios.forEach(radio => {
            radio.addEventListener('change', async () => {
                const limit = parseInt(radio.value);

                if (limit === 19) {
                    const confirmed = await window.Modal.confirm(
                        window.locale.t('editor.adult_confirm_body'),
                        window.locale.t('editor.adult_confirm_title')
                    );
                    if (!confirmed) {
                        // Revert to 0 or previous?
                        // Simple revert to 15 or 0
                        this.container.querySelector('input[name="age-limit"][value="0"]').checked = true;
                        badgeAdult.classList.add('hidden');
                        return;
                    }
                    badgeAdult.classList.remove('hidden');
                } else {
                    badgeAdult.classList.add('hidden');
                }
            });
        });

        // Trigger Guide
        const btnGuide = this.container.querySelector('#btn-age-guide');
        if (btnGuide) {
            btnGuide.onclick = () => {
                // Dynamically import or use global if available
                import('./AgeRatingGuideModal.js').then(module => {
                    new module.AgeRatingGuideModal().open();
                });
            };
        }

        const checkAI = this.container.querySelector('#input-is-ai');
        const badgeAI = this.container.querySelector('#badge-ai');
        checkAI.onchange = () => {
            badgeAI.classList.toggle('hidden', !checkAI.checked);
        };

        // Cover Preview Logic
        const inputCoverUrl = this.container.querySelector('#input-cover-url');
        const previewCover = this.container.querySelector('#preview-cover');
        const noCoverPlaceholder = previewCover.nextElementSibling;

        inputCoverUrl.oninput = () => {
            if (inputCoverUrl.value) {
                previewCover.src = inputCoverUrl.value;
                previewCover.classList.remove('hidden');
                noCoverPlaceholder.classList.add('hidden');
            } else {
                previewCover.classList.add('hidden');
                noCoverPlaceholder.classList.remove('hidden');
            }
        };

        // Upload Cover Logic (Placeholder - needs storage logic if we want real upload here. 
        // For MVP refactor, we reuse AuthorStudio logic or simple file reader preview)
        const fileInput = this.container.querySelector('#input-cover-file');
        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Preview immediately
            const reader = new FileReader();
            reader.onload = (ev) => {
                previewCover.src = ev.target.result;
                previewCover.classList.remove('hidden');
                noCoverPlaceholder.classList.add('hidden');
                // Note: We need to upload this on Save. 
                // For now, let's just alert that upload is separate or implement upload helper.
                this.pendingCoverFile = file;
            };
            reader.readAsDataURL(file);
        };

        // CSV Export (Admin)
        if (isAdmin && this.mode === 'edit') {
            const btnCsv = this.container.querySelector('#btn-download-csv');
            if (btnCsv) {
                btnCsv.onclick = () => {
                    const data = [{
                        internalId: this.seriesId,
                        title: this.data.title,
                        author: this.data.authorName,
                        isbn: this.data.isbn || '',
                        uci: this.data.uci || ''
                    }];
                    const csv = CSVUtils.toCSV(data, ['internalId', 'title', 'author', 'isbn', 'uci']);
                    CSVUtils.download(csv, `series_meta_${this.seriesId}.csv`);
                };
            }
        }

        // Episode Tab Actions
        const btnLaunchEditor = this.container.querySelector('#btn-launch-editor');
        if (btnLaunchEditor) {
            btnLaunchEditor.onclick = () => {
                // Open Editor Route
                // Ideally in same window but user feels "Editor" is heavy.
                // Router navigate.
                window.router.navigate(`/studio/series/${this.seriesId}/editor`);
                this.close(); // Close modal which is now outdated
            };
        }

        // Type Selection Logic (Restriction for Da Vinci)
        const typeRadios = this.container.querySelectorAll('input[name="series-type"]');
        let currentType = this.container.querySelector('input[name="series-type"]:checked')?.value || 'webtoon';

        typeRadios.forEach(radio => {
            radio.addEventListener('change', (e) => {
                if (radio.value === 'davinci') {
                    const isSuperAdmin = window.user && (window.user.isSuperAdmin || ['admin', 'super_admin', 'author'].includes(window.user.role) || window.user.email === 'hegler.kim@gmail.com');

                    if (!isSuperAdmin) {
                        // Revert Selection
                        const prevRadio = this.container.querySelector(`input[name="series-type"][value="${currentType}"]`);
                        if (prevRadio) prevRadio.checked = true;

                        // Show Alert Template
                        window.Modal.alert(
                            `<div class="text-center p-2">
                                <span class="material-symbols-outlined text-4xl text-rose-500 mb-3 block mx-auto">lock_clock</span>
                                <h3 class="font-bold text-lg mb-2 text-white">${window.locale.t('editor.davinci_lock_title')}</h3>
                                <p class="text-zinc-400 text-sm leading-relaxed">
                                   ${window.locale.t('editor.davinci_lock_desc')}
                                </p>
                              </div>`,
                            "접근 제한"
                        );
                        return;
                    }
                }
                // Update current type if allowed
                currentType = radio.value;
            });
        });

        // SAVE BUTTON
        this.container.querySelector('.btn-save').onclick = () => this.save();
    }



    async save() {
        const title = this.container.querySelector('#input-title').value.trim();
        if (!title) {
            window.Modal.alert("Title is required."); // Or add key if needed, or use existing common error
            return;
        }

        const btnSave = this.container.querySelector('.btn-save');
        btnSave.disabled = true;
        btnSave.innerHTML = `<span class="material-symbols-outlined animate-spin text-[18px]">progress_activity</span> ${window.locale.t('editor.saving')}`;

        // Gather Data
        const updates = {
            title: title,
            // [CRITICAL FIX] Data Integrity Lock: Prevent Type Switching in Edit Mode
            // In Edit Mode, we MUST use the original immutable type from this.data.type
            // The UI input is ignored to prevent accidental or malicious type changes.
            type: this.mode === 'create'
                ? (this.container.querySelector('input[name="series-type"]:checked')?.value || 'webtoon')
                : (this.data.type || 'webtoon'),
            description: this.container.querySelector('#input-desc').value || '',
            tags: this.container.querySelector('#input-tags').value.split(',').map(t => t.trim()).filter(Boolean),
            // displayMode removed

            // [Age Logic]
            ageLimit: parseInt(this.container.querySelector('input[name="age-limit"]:checked')?.value || 0),
            isAdult: parseInt(this.container.querySelector('input[name="age-limit"]:checked')?.value || 0) === 19,

            isAI: this.container.querySelector('#input-is-ai').checked || false,
            coverImage: this.container.querySelector('#input-cover-url').value || this.data.coverImage || "",
            updatedAt: serverTimestamp()
        };

        // Handle Admin Fields (Only if Elements Exist)
        const inputIsbn = this.container.querySelector('#input-isbn');
        if (inputIsbn) updates.isbn = inputIsbn.value.trim();

        const inputUci = this.container.querySelector('#input-uci');
        if (inputUci) updates.uci = inputUci.value.trim();

        // Handle Free Limit (Allow 0)
        const inputFreeLimit = this.container.querySelector('#input-free-limit');
        if (inputFreeLimit && inputFreeLimit.value !== '') {
            updates.customFreeLimit = parseInt(inputFreeLimit.value);
        } else if (inputFreeLimit && inputFreeLimit.value === '') {
            // Explicitly remove if cleared? Firestore needs deleteField() or allow undefined.
            // Usually setting to null or specific logic.
            // For now, let's assuming overwriting with null is safer or check legacy.
            updates.customFreeLimit = null; // or deleteField() if imported
        }

        // Handle Cover Upload (If pending)
        if (this.pendingCoverFile) {
            try {
                // Inline upload logic reused (simplified)
                // Ideally call a Service. 
                // Creating a simplified upload here to keep "Atomic"
                const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
                const storage = getStorage(app);

                // [FIX] Compress to WebP before upload (1280px max width, 80% quality)
                const compressedBlob = await convertToWebP(this.pendingCoverFile, { maxWidth: 1280, quality: 0.8 });

                // [FIX] Enforce .webp extension
                const safeName = this.pendingCoverFile.name.replace(/\.[^/.]+$/, "") + ".webp";
                const path = `covers/${this.seriesId || 'new'}_${Date.now()}_${safeName}`;

                const storageRef = ref(storage, path);

                // [FIX] Enforce image/webp contentType for Storage Rules
                await uploadBytes(storageRef, compressedBlob, { contentType: 'image/webp' });

                updates.coverImage = await getDownloadURL(storageRef);
            } catch (e) {
                console.error("Cover Upload Failed", e);
                window.Modal.toast(window.locale.t('editor.upload_fail'));
            }
        }

        try {
            if (this.mode === 'create') {
                updates.authorId = window.user.uid;
                updates.authorName = window.user.displayName || 'Author';
                updates.createdAt = serverTimestamp();
                updates.stats = { viewCount: 0, likeCount: 0 };
                updates.status = 'draft';

                // [Autogenerate Internal ID for Admin reference?]
                // We use Doc ID as Internal ID usually, but requirement said SW-YYYYMMDD...
                // Let's rely on Doc ID for now unless Admin explicitly sets a field.

                await addDoc(collection(db, "series"), updates);
            } else {
                await updateDoc(doc(db, "series", this.seriesId), updates);
            }

            window.Modal.toast(`${this.mode === 'create' ? window.locale.t('editor.create') : window.locale.t('editor.saved')}`);
            this.close();
        } catch (e) {
            console.error(e);
            window.Modal.alert("Error: " + e.message);
        } finally {
            if (this.container) {
                btnSave.disabled = false;
                btnSave.innerHTML = `<span class="material-symbols-outlined text-[18px]">save</span> ${window.locale.t('editor.save')}`;
            }
        }
    }
}
